/**
 * Polyfills for older browser compatibility
 * This file must be imported FIRST in main.tsx before any other imports
 */

/**
 * Promise.withResolvers polyfill (ES2024)
 * Required for pdfjs-dist v5.x which uses this API internally
 *
 * Browser support without polyfill:
 * - Chrome 119+ (Nov 2023)
 * - Firefox 121+ (Dec 2023)
 * - Safari 17.4+ (Mar 2024)
 * - Edge 119+
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/withResolvers
 */
/**
 * Ensures Promise.withResolvers is available
 * Can be called multiple times safely (idempotent)
 */
export const ensurePromiseWithResolvers = (): void => {
  if (typeof Promise.withResolvers === "undefined") {
    Promise.withResolvers = function <T>(): {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: unknown) => void;
    } {
      let resolve!: (value: T | PromiseLike<T>) => void;
      let reject!: (reason?: unknown) => void;
      const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      return { promise, resolve, reject };
    };
  }
};

// Apply polyfill immediately when this module is imported
ensurePromiseWithResolvers();

// Extend Promise interface for TypeScript
declare global {
  interface PromiseConstructor {
    withResolvers<T>(): {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: unknown) => void;
    };
  }
}
